import type {
  ConfigFormat,
  McpClientId,
} from "@arcadeai/ui-kit/mcp-gateway-connect";
import { useCallback } from "react";
import { useOnboardingTelemetry } from "@/app/onboarding-first-win/hooks/use-onboarding-telemetry";

type UseOnboardingConnectTelemetryParams = {
  clientForNav?: McpClientId;
};

type OnboardingConnectTelemetry = {
  captureClientSelected: (clientId: McpClientId) => void;
  captureConfigCopied: (clientId: McpClientId, format: ConfigFormat) => void;
  captureInstallDeepLink: (clientId: McpClientId, gatewaySlug: string) => void;
  capturePromptCopied: () => void;
  capturePromptDeepLink: () => void;
};

/** Connect-step action analytics invoked from UI handlers. */
export function useOnboardingConnectTelemetry({
  clientForNav,
}: UseOnboardingConnectTelemetryParams): OnboardingConnectTelemetry {
  const { captureOnboardingEvent } = useOnboardingTelemetry();

  const captureClientSelected = useCallback(
    (clientId: McpClientId) => {
      captureOnboardingEvent("Onboarding client selected", {
        client_id: clientId,
        current_step: "connect",
      });
    },
    [captureOnboardingEvent]
  );

  const captureConfigCopied = useCallback(
    (clientId: McpClientId, format: ConfigFormat) => {
      captureOnboardingEvent("Onboarding config copied", {
        client_id: clientId,
        config_format: format,
        current_step: "connect",
      });
    },
    [captureOnboardingEvent]
  );

  const captureInstallDeepLink = useCallback(
    (clientId: McpClientId, gatewaySlug: string) => {
      captureOnboardingEvent("Onboarding install deep link fired", {
        client_id: clientId,
        gateway_slug: gatewaySlug,
        current_step: "connect",
      });
    },
    [captureOnboardingEvent]
  );

  const capturePromptCopied = useCallback(() => {
    if (clientForNav) {
      captureOnboardingEvent("Onboarding prompt copied", {
        client_id: clientForNav,
        current_step: "connect",
      });
    }
  }, [captureOnboardingEvent, clientForNav]);

  const capturePromptDeepLink = useCallback(() => {
    if (clientForNav) {
      captureOnboardingEvent("Onboarding prompt deep link fired", {
        client_id: clientForNav,
        current_step: "connect",
      });
    }
  }, [captureOnboardingEvent, clientForNav]);

  return {
    captureClientSelected,
    captureConfigCopied,
    captureInstallDeepLink,
    capturePromptCopied,
    capturePromptDeepLink,
  };
}
